import {CM_LOGGER_TEXTURES} from "@app/common/services/logging"
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    computed,
    DestroyRef,
    effect,
    inject,
    input,
    OnDestroy,
    output,
    signal,
    viewChild,
} from "@angular/core"
import {CanvasPhysicalInfo} from "@common/components/canvas"
import * as paper from "paper"
import {Subscription} from "rxjs"
import {Box2, Matrix3x2, Size2Like, Vector2} from "@cm/math"
import {OperatorToolboxBase} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator-toolbox-base"
import {TextureEditorSettings} from "@app/textures/texture-editor/texture-editor-settings"
import {HalPainterImageStretchTiled} from "@app/textures/texture-editor/tiling-canvas/hal/hal-painter-image-stretch-tiled"
import {AsyncReentrancyGuard} from "@cm/utils/async-reentrancy-guard"
import {TextureEditorCallback} from "@app/textures/texture-editor/texture-editor-callback"
import {Operator} from "@app/textures/texture-editor/operator-stack/operators/abstract-base/operator"
import {MipLevelRegion} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/image-op-descriptor"
import {AnyImageOpHal, ImageOpHal} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-op-hal"
import {NotificationsService} from "@common/services/notifications/notifications.service"
import {simpleGraphFromImageOpHalData} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/export-utils"
import {ExporterYed} from "@platform/helpers/simple-graph"
import {downloadFromMemory} from "@common/helpers/utils/download-from-memory"
import {HalImageRef} from "@cm/hal/hal-ref"
import {RegionLockedHalImageRef} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/region-locked-hal-image-ref"
import {AbortUpdateError} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/hal/image-update-scheduler"
import {RulerWorkspaceComponent} from "@common/components/canvas/ruler-workspace/ruler-workspace.component"
import {CanvasComponent} from "@common/components/canvas/canvas/canvas.component"
import {WebGl2Canvas} from "@common/helpers/webgl2-canvas/webgl2-canvas.component"
import {HalContext} from "@cm/hal"
import {takeUntilDestroyed} from "@angular/core/rxjs-interop"

const PRIORITY_PREVIEW = 1000
const PRIORITY_FULL_UPDATE = 0

@Component({
    changeDetection: ChangeDetectionStrategy.OnPush,
    selector: "cm-tiling-canvas",
    templateUrl: "./tiling-canvas.component.html",
    styleUrls: ["./tiling-canvas.component.scss"],
    imports: [RulerWorkspaceComponent, CanvasComponent],
})
export class TilingCanvasComponent implements AfterViewInit, OnDestroy {
    private readonly logger = inject(CM_LOGGER_TEXTURES)
    readonly $canvas = viewChild.required<CanvasComponent>("canvas")
    readonly $rulerWorkspace = viewChild.required<RulerWorkspaceComponent>("rulerWorkspace")
    readonly $callback = input.required<TextureEditorCallback>({alias: "callback"})
    readonly $physicalInfo = input.required<CanvasPhysicalInfo>({alias: "physicalInfo"})
    readonly $suspendRedraws = input(false, {alias: "suspendRedraws"})

    readonly $currentImage = signal<HalImageRef | null>(null)
    readonly $isSrgb = signal<boolean>(false)

    readonly frameDrawn = output<void>()
    readonly loadError = output<Error>()
    readonly $onAboutToBeDestroyed = input.required<() => Promise<void>>({alias: "onAboutToBeDestroyed"}) // awaited before disposing the WebGL2 context

    readonly $gridActive = input(false, {alias: "gridActive"})
    readonly $gridOutlineActive = input(false, {alias: "gridOutlineActive"})
    readonly $showAtlas = input(false, {alias: "showAtlas"})

    get gridActive(): boolean {
        return this._gridActive
    }

    get showAtlas(): boolean {
        return this._showAtlas
    }

    set showAtlas(value: boolean) {
        if (this._showAtlas === value) {
            return
        }
        this._showAtlas = value
        this.requestRedraw()
    }

    private readonly notificationService = inject(NotificationsService)
    private readonly destroyRef = inject(DestroyRef)

    constructor() {
        effect(() => {
            const gridActive = this.$gridActive()
            if (this._gridActive === gridActive) {
                return
            }
            this._gridActive = gridActive
            this.requestRedraw()
        })
        effect(() => {
            const gridOutlineActive = this.$gridOutlineActive()
            this._gridOutlineActive = gridOutlineActive
            this.updateGrid()
        })
        effect(() => {
            const showAtlas = this.$showAtlas()
            if (this._showAtlas === showAtlas) {
                return
            }
            this._showAtlas = showAtlas
            this.requestRedraw()
        })
    }

    ngAfterViewInit(): void {
        this.$canvas().beginPaperCreation()
        this.gridLinesGroup = new paper.Group()
        this.gridLinesGroup.name = "GridLines"

        const webGl2Canvas = new WebGl2Canvas(this.$canvas().htmlCanvasElement, {
            physicalCache: {
                sizeInBytes: TextureEditorSettings.PhysicalCacheSizeInBytes,
            },
            virtualCache: {
                sizeInBytes: TextureEditorSettings.VirtualCacheSizeInBytes,
                numAtlasPages: TextureEditorSettings.NumVirtualPages,
                maxNumAtlases: TextureEditorSettings.MaxNumAtlases,
            },
        })
        webGl2Canvas.resized.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(() => this.requestRedraw())
        this.$webGl2Canvas.set(webGl2Canvas)
        this.halPainterImageDrawTiled = new HalPainterImageStretchTiled(webGl2Canvas.halContext)
        this.$canvas().redraw.subscribe(() => {
            this._lastDrawPromise = this.drawPreviewAndTriggerFullUpdate()
        })

        this.$rulerWorkspace().$measurementUnit.set(TextureEditorSettings.DefaultMeasurementUnit)
    }

    ngOnDestroy(): void {
        if (!this.$webGl2Canvas() || !this.halPainterImageDrawTiled) {
            throw new Error("TilingCanvasComponent: Already disposed")
        }
        this.releaseResultImage()
        this.$currentCanvasUpdate()?.abortController.abort()
        const pendingCanvasWork = Promise.allSettled([this._lastDrawPromise, this.$currentCanvasUpdate()?.promisedUpdate])
        void this.disposeGlResourcesOncePendingWorkSettles(pendingCanvasWork)
    }

    private async disposeGlResourcesOncePendingWorkSettles(pendingCanvasWork: Promise<unknown>): Promise<void> {
        await pendingCanvasWork
        await this.$onAboutToBeDestroyed()() // operator-stack HAL resources must be disposed while the context is still alive

        this.halPainterImageDrawTiled?.dispose()
        this.halPainterImageDrawTiled = null

        this.$webGl2Canvas.update((webgl2Canvas) => {
            webgl2Canvas?.dispose()
            return null
        })
    }

    get toolbox(): OperatorToolboxBase<Operator> | null {
        return this.operatorToolbox
    }

    set toolbox(value: OperatorToolboxBase<Operator> | null) {
        if (this.operatorToolbox === value) {
            return
        }
        this.operatorToolbox = value
        this.$canvas().$toolbox.set(value)
    }

    get displayGamma(): number {
        return this._displayGamma
    }

    set displayGamma(value: number) {
        this._displayGamma = value
        this.requestRedraw()
    }

    requestRedraw() {
        this.$canvas().requestRedraw()
    }

    // get isRedrawRequested(): boolean {
    //     return this.$canvasBase().isRedrawRequested
    // }

    setResultImage(resultImage: AnyImageOpHal | null) {
        if (this._imageOpHal === resultImage) {
            this.resetPreview()
            this.requestRedraw()
            return
        }
        // console.log("TilingCanvasComponent.setResultImage begin")
        this.releaseResultImage()
        this._imageOpHal = resultImage
        if (this._imageOpHal) {
            this._imageOpHal.addRef("TilingCanvas.setResultImage")
            // this._subscriptions.push(
            //     this._imageOpHal.regionsInvalidated.subscribe(() => {
            //         this.resetPreview()
            //         this.$canvasBase().requestRedraw()
            //     }),
            //     // this._imageOpHal.pageContentUpdated.subscribe(() => {
            //     //     this.$canvasBase().requestRedraw()
            //     // }),
            // )
            const canvas = this.$canvas()
            canvas.$canvasBounds.set({
                width: this._imageOpHal.descriptor.width,
                height: this._imageOpHal.descriptor.height,
            })
            const initialResolution = 256 // this should be the page size of the used data-object
            this._previewMipLevel = Math.max(
                0,
                Math.min(
                    this._imageOpHal.numMipLevels - 1,
                    Math.ceil(Math.log2(Math.max(this._imageOpHal.descriptor.width, this._imageOpHal.descriptor.height) / initialResolution)),
                ),
            )
        }
        this.updateGrid()
        this.requestRedraw()
        // console.log("TilingCanvasComponent.setResultImage end")
    }

    private releaseResultImage() {
        this._subscriptions.forEach((subscription) => subscription.unsubscribe())
        this._subscriptions = []
        this._imageOpHal?.release("TilingCanvas.setResultImage")
        this._imageOpHal = null
        this.resetPreview()
    }

    setMessage(message: string | undefined): void {
        this.$message.set(message)
    }

    private resetPreview() {
        this._currentResultImageUpdatedOutput?.release()
        this._currentResultImageUpdatedOutput = null
    }

    private async drawPreviewAndTriggerFullUpdate(): Promise<void> {
        return this._updateAndDrawGate
            .startAndRejectCurrent(async () => {
                // console.log("drawPreviewAndTriggerFullUpdate - begin")
                const resultImage = this._imageOpHal
                let canvasUpdate: CanvasUpdateInfo | null = null
                if (resultImage) {
                    resultImage.addRef("TilingCanvas.drawPreviewAndTriggerFullUpdate")
                    // update preview if needed
                    let justDrewPreview = false
                    if (!this._currentResultImageUpdatedOutput) {
                        this.$isFetchingPreview.set(true)
                        const previewPromise = this.updateCurrentResultImageRegion(resultImage, this._previewMipLevel, PRIORITY_PREVIEW)
                            .then((updatedRegion) => {
                                if (this._imageOpHal === resultImage) {
                                    this._currentResultImageUpdatedOutput = updatedRegion
                                } else {
                                    updatedRegion.release()
                                }
                            })
                            .finally(() => this.$isFetchingPreview.set(false))
                        // wait for preview to be ready
                        await previewPromise
                        justDrewPreview = true
                    }
                    // trigger full update
                    const fullMipLevel = this.computeCurrentSourceMipLevel()
                    if (this._imageOpHal === resultImage && (fullMipLevel !== this._previewMipLevel || !justDrewPreview)) {
                        const abortController = new AbortController()
                        resultImage.addRef("TilingCanvas.updateCurrentResultImageRegion")
                        canvasUpdate = {
                            promisedUpdate: this.updateCurrentResultImageRegion(resultImage, fullMipLevel, PRIORITY_FULL_UPDATE, abortController.signal)
                                .then((updatedRegion) => {
                                    this.drawCurrentImage()
                                    updatedRegion.release()
                                })
                                .catch((e) => {
                                    // swallow abort errors
                                    if (!(e instanceof AbortUpdateError)) {
                                        throw e
                                    }
                                })
                                .finally(() => {
                                    resultImage.release("TilingCanvas.updateCurrentResultImageRegion")
                                    if (this.$currentCanvasUpdate() === canvasUpdate) {
                                        this.$currentCanvasUpdate.set(null)
                                    }
                                }),
                            abortController: abortController,
                        }
                    }
                }
                const prevCanvasUpdate = this.$currentCanvasUpdate()
                this.$currentCanvasUpdate.set(canvasUpdate)
                // abort previous canvas update if any
                prevCanvasUpdate?.abortController.abort()
                // allow some time for the frame to render; if it takes too long we simply draw what we got this far
                if (canvasUpdate) {
                    const maxFrameTimer = new Promise((resolve) => setTimeout(resolve, this.maxFrameTimeMs))
                    const timedOut = await Promise.race([canvasUpdate.promisedUpdate.then(() => false), maxFrameTimer.then(() => true)])
                    if (timedOut) {
                        // if we timed out, we request another redraw to continue rendering the image once the update is done
                        setTimeout(() => this.requestRedraw(), this.updateLongFrameIntervalMs)
                    }
                }
                // draw the frame
                this.drawCurrentImage()
                // console.log("drawPreviewAndTriggerFullUpdate - end")
                resultImage?.release("TilingCanvas.drawPreviewAndTriggerFullUpdate")
            })
            .catch((e) => {
                if (e instanceof AsyncReentrancyGuard.RejectionError) {
                    this.logger.trace("drawPreviewAndTriggerFullUpdate - cancelled")
                } else {
                    setTimeout(() => {
                        throw e
                    }, 0) // Throws as an unhandled exception
                }
            })
    }

    // private async updateAndRedraw(resultImage: ImageOpHal, mipLevel: number, abortSignal?: AbortSignal) {
    //     const updatedRegion = await this.updateCurrentResultImageRegion(resultImage, mipLevel, abortSignal)
    //     if (updatedRegion) {
    //         await this.drawCurrentImage(updatedRegion.halImageRef, mipLevel)
    //         updatedRegion.release()
    //     }
    // }

    private async updateCurrentResultImageRegion(
        resultImage: AnyImageOpHal,
        mipLevel: number,
        priority = 0,
        abortSignal?: AbortSignal,
    ): Promise<RegionLockedHalImageRef> {
        const webGlCanvasComponent = this.$webGl2Canvas()
        if (!webGlCanvasComponent) {
            throw new Error("TilingCanvasComponent: WebGlCanvas not initialized")
        }

        const halContext = webGlCanvasComponent.halContext

        const outputWidth = webGlCanvasComponent.backBufferImage.ref.descriptor.width
        const outputHeight = webGlCanvasComponent.backBufferImage.ref.descriptor.height
        const fudgeFactor = 2
        const maxScreenPages =
            fudgeFactor * Math.ceil(outputWidth / halContext.descriptor.atlasPageSize) * Math.ceil(outputHeight / halContext.descriptor.atlasPageSize) * 4 // x4 because we might at most oversample x2 in each dimension
        if (TextureEditorSettings.PageDescriptorWarnLimit !== maxScreenPages) {
            this.logger.debug(`Number of worst-case output pages: ${maxScreenPages}`)
            TextureEditorSettings.PageDescriptorWarnLimit = maxScreenPages // let's warn if some image-op requests more pages than the number of worst-case output pages
        }
        const maxScreenPixels = fudgeFactor * outputWidth * outputHeight * 4 // x4 because we might at most oversample x2 in each dimension
        if (TextureEditorSettings.RegionPixelsWarnLimit !== maxScreenPixels) {
            TextureEditorSettings.RegionPixelsWarnLimit = maxScreenPixels // let's warn if some image-op requests more pixels than the number of worst-case output pixels
        }

        const transform = Matrix3x2.fromMatrix3x2Like(this.$canvas().$viewTransform())
        const invTransform = transform.inverse()
        const mipLevelScale = 1 << mipLevel
        const p00 = invTransform.multiplyVector(new Vector2(0, 0)).div(mipLevelScale).floor()
        const p11 = invTransform.multiplyVector(new Vector2(outputWidth, outputHeight)).div(mipLevelScale).ceil()
        const mipLevelRegion: MipLevelRegion = {
            x: p00.x,
            y: p00.y,
            width: p11.x - p00.x,
            height: p11.y - p00.y,
            mipLevel: mipLevel,
        }
        if (!this._gridActive) {
            // when grid is not active we clamp to image bounds
            const mipLevelSize = resultImage.getMipLevelSize(mipLevel)
            mipLevelRegion.x = Math.max(0, mipLevelRegion.x)
            mipLevelRegion.y = Math.max(0, mipLevelRegion.y)
            mipLevelRegion.width = Math.min(mipLevelSize.width - mipLevelRegion.x, mipLevelRegion.width)
            mipLevelRegion.height = Math.min(mipLevelSize.height - mipLevelRegion.y, mipLevelRegion.height)
        }

        this.logger.trace(`redraw: ${JSON.stringify(mipLevelRegion)}`)
        const start = performance.now()
        let updatedRegion: RegionLockedHalImageRef
        try {
            updatedRegion = await resultImage.getUpdatedHalImage(mipLevelRegion, {priority, abortSignal})
        } catch (e) {
            if (e instanceof Error && e.name !== "AbortError" && e.name !== "AbortUpdateError") {
                this.logger.error("Error in getUpdatedHalImage", e)
                this.notificationService.showError("Error while updating result image: " + (e instanceof Error ? e.message : String(e)))
                this.loadError.emit(e)
                ImageOpHal.printDebugInfo()
            }
            throw e
        }
        this.logger.trace(
            `updateRegions of region [${mipLevelRegion.x}, ${mipLevelRegion.y}, ${mipLevelRegion.width}, ${mipLevelRegion.height}] on mip-level ${mipLevelRegion.mipLevel} took ${performance.now() - start}ms`,
        )
        return updatedRegion
    }

    private computeCurrentSourceMipLevel() {
        if (!this._imageOpHal) {
            return 0
        }
        const webGl2Canvas = this.$webGl2Canvas()
        if (!webGl2Canvas) {
            throw new Error("TilingCanvasComponent: WebGlCanvas not initialized")
        }
        const zoomFudgeFactor = this.$canvas().devicePixelRatio / webGl2Canvas.devicePixelRatio // TODO is this correct ?
        const zoom = this.$canvas().$physicalZoomFactor() / zoomFudgeFactor
        return Math.max(
            TextureEditorSettings.MinMipLevel,
            Math.min(this._imageOpHal.numMipLevels - 1, Math.floor(Math.log2(TextureEditorSettings.PixelSize / zoom))),
        )
    }

    private drawCurrentImage() {
        if (!this.halPainterImageDrawTiled || !this._currentResultImageUpdatedOutput) {
            return
        }
        const outputImageRef = this._currentResultImageUpdatedOutput.halImageRef
        outputImageRef.addRef()
        const mipLevel = this.computeCurrentSourceMipLevel()
        const canvas = this.$canvas()
        const webGl2Canvas = this.$webGl2Canvas()
        if (!webGl2Canvas) {
            throw new Error("TilingCanvasComponent: WebGlCanvas not initialized")
        }
        const devicePixelRatio = webGl2Canvas.devicePixelRatio
        const transform = Matrix3x2.fromMatrix3x2Like([devicePixelRatio, 0, 0, devicePixelRatio, 0, 0]).append(canvas.$viewTransform())
        const outputWidth = webGl2Canvas.backBufferImage.ref.descriptor.width
        const outputHeight = webGl2Canvas.backBufferImage.ref.descriptor.height
        webGl2Canvas.clearBackBuffer()
        const imageBounds = this.halPainterImageDrawTiled.paint(
            webGl2Canvas.backBufferImage.ref,
            outputImageRef.ref,
            this._gridActive ? new Box2(0, 0, outputWidth, outputHeight) : undefined,
            {
                sourceMipLevel: mipLevel,
                transform: transform,
                addressMode: "clamp",
            },
        )
        outputImageRef.release()
        canvas.$canvasBounds.set(imageBounds)
        webGl2Canvas.presentBackBuffer(this.$isSrgb(), this._displayGamma)
        this.frameDrawn.emit()
    }

    loadingCompleted(): void {
        // This is required to redraw the grid in case the texture size changes while the grid being active.
        if (this._gridActive) {
            this._gridActive = false
            this._gridActive = true
            this.updateGrid()
        }
        this.requestRedraw()
    }

    private updateGrid(): void {
        if (this.gridLinesGroup) {
            this.$canvas().beginPaperCreation()
            this.gridLinesGroup.removeChildren()
            if (this._gridOutlineActive) {
                const canvasBounds = this.tileBounds
                const halfGridDisplaySize = 10
                for (let x = -halfGridDisplaySize; x <= halfGridDisplaySize + 1; x++) {
                    const p0 = new paper.Point(x * canvasBounds.width, -halfGridDisplaySize * canvasBounds.height)
                    const p1 = new paper.Point(x * canvasBounds.width, (halfGridDisplaySize + 1) * canvasBounds.height)
                    const linePath = new paper.Path.Line(p0, p1)
                    linePath.strokeColor = new paper.Color(1, 1, 1, 1)
                    linePath.strokeWidth = 1
                    linePath.strokeScaling = false
                    this.gridLinesGroup.addChild(linePath)
                }
                for (let y = -halfGridDisplaySize; y <= halfGridDisplaySize + 1; y++) {
                    const p0 = new paper.Point(-halfGridDisplaySize * canvasBounds.width, y * canvasBounds.height)
                    const p1 = new paper.Point((halfGridDisplaySize + 1) * canvasBounds.width, y * canvasBounds.height)
                    const linePath = new paper.Path.Line(p0, p1)
                    linePath.strokeColor = new paper.Color(1, 1, 1, 1)
                    linePath.strokeWidth = 1
                    linePath.strokeScaling = false
                    this.gridLinesGroup.addChild(linePath)
                }
            }
        }
    }

    get halContext(): HalContext {
        const webGl2Canvas = this.$webGl2Canvas()
        if (!webGl2Canvas) {
            throw new Error("TilingCanvasComponent: WebGlCanvas not initialized")
        }
        return webGl2Canvas.halContext
    }

    get tileBounds() {
        const halImageSize: Size2Like = this._imageOpHal
            ? this._imageOpHal.descriptor
            : (this.$currentImage()?.ref.descriptor ?? {
                  width: 0,
                  height: 0,
              })
        return new Box2(0, 0, halImageSize.width, halImageSize.height)
    }

    exportResultImageGraph() {
        if (!this._imageOpHal) {
            return
        }
        const simpleGraph = simpleGraphFromImageOpHalData(this._imageOpHal.data)
        const exporter = new ExporterYed()
        const xml = exporter.export(simpleGraph)
        downloadFromMemory("result-image-graph.graphml", xml)
    }

    readonly $message = signal<string | undefined>(undefined)

    private readonly $webGl2Canvas = signal<WebGl2Canvas | null>(null)
    // private _loadImageGate = new AsyncReentrancyGuard.PromiseGate()
    private halPainterImageDrawTiled: HalPainterImageStretchTiled | null = null
    private _lastDrawPromise: Promise<void> = Promise.resolve()
    private _displayGamma = 1
    private gridLinesGroup: paper.Group | null = null
    private _gridOutlineActive = false
    private _gridActive = false
    private _showAtlas = false
    private operatorToolbox: OperatorToolboxBase<Operator> | null = null

    private _updateAndDrawGate = new AsyncReentrancyGuard.PromiseGate<void>()

    private _imageOpHal: AnyImageOpHal | null = null
    private _currentResultImageUpdatedOutput: RegionLockedHalImageRef | null = null
    private _previewMipLevel = 0
    private _subscriptions: Subscription[] = []

    private maxFrameTimeMs = 50
    private updateLongFrameIntervalMs = 200

    private readonly $currentCanvasUpdate = signal<CanvasUpdateInfo | null>(null)
    private readonly $isFetchingPreview = signal(false)

    protected readonly $showSpinner = computed(() => this.$currentCanvasUpdate() !== null || this.$isFetchingPreview())
}

type CanvasUpdateInfo = {
    promisedUpdate: Promise<void>
    abortController: AbortController
}
